import { EyeInvisibleFilled } from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Space,
  Switch,
  Tooltip,
  Typography,
} from "antd";
import { useEffect, useRef, useState } from "react";
import AddIcon from "../../../../assets/icons/AddIcon";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import DoubleTick from "../../../../assets/icons/DoubleTick";
import EditIcon from "../../../../assets/icons/EditIcon";
import PercentageIcon from "../../../../assets/icons/PercentageIcon";
import XIcon from "../../../../assets/icons/XIcon";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import Loader from "../../../../components/ui/Loader";
import {
  useGetPayrollSettingQuery,
  useUpdateGrossComponentMutation,
} from "../../../../features/payrollSetting/payrollSettingApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { generateUUID } from "../../../../utils/number";
import { convertToSnakeCase } from "../../../../utils/utils";
import { payrollSettingsTabList } from "./payrollSetting";

const { Title, Text } = Typography;
const roundButtonClass = "!w-7 !h-7 middle-button p-[7px] rounded-full";

type SalaryBreakdownType = {
  id?: number;
  key: string;
  title: string | null;
  value: number;
  is_default?: number;
  is_active: number;
  taxable: number;
  type?: "ADD" | "EDIT" | "DELETE";
  generatedId?: string;
};

type CreateDataType = Partial<SalaryBreakdownType>;

export default function SalaryBreakdown() {
  const inputRef = useRef<any>(null);
  const [createdData, setCreatedData] = useState<CreateDataType | undefined>(
    undefined
  );
  const [salaryBreakdown, setSalaryBreakdown] =
    useState<SalaryBreakdownType[]>();

  const { open, handleOpen, handleClose } = useModal<
    Record<string, string>,
    "CONFIRM"
  >();
  const { data, isLoading, isError, error, refetch } =
    useGetPayrollSettingQuery();

  const [
    updateGrossComponent,
    {
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      error: updateError,
      isSuccess: isUpdateSuccess,
    },
  ] = useUpdateGrossComponentMutation();
  useError(isError, error, "Get salary breakdown data fail");
  useError(isUpdateError, updateError, "Update salary breakdown fail");

  const getSalaryData = data?.data?.payroll_setting;

  const totalPercentage = salaryBreakdown?.reduce(
    (acc, curr) =>
      acc + (curr.is_active && curr.type !== "DELETE" ? curr.value : 0),
    0
  );

  const findOldSalary = (id: number) => {
    return getSalaryData?.salary_breakdown?.find((item) => item?.id === id)
      ?.value;
  };

  const handleCreatedData = <K extends keyof CreateDataType>(
    keyItem: K,
    value: CreateDataType[K]
  ) => {
    setCreatedData(
      (prev) =>
        ({
          ...prev,
          [keyItem]: value !== undefined ? value : prev?.[keyItem] ?? 0,
          key:
            keyItem === "title"
              ? convertToSnakeCase((value as CreateDataType["title"]) || "")
              : prev?.key,
          // id: prev?.id || generateUUID(),
          type: prev?.id ? "EDIT" : "ADD",
        } as CreateDataType)
    );
  };

  const handleSalaryBreakdown = <K extends keyof SalaryBreakdownType>(
    systemKey: string,
    key: K,
    value: SalaryBreakdownType[K]
  ) => {
    setSalaryBreakdown((prev) =>
      prev?.map((item) => {
        if (item?.key === systemKey) {
          return {
            ...item,
            type:
              item?.id && findOldSalary(item?.id) !== value
                ? "EDIT"
                : item?.type === "ADD"
                ? "ADD"
                : undefined,
            [key]: value,
          };
        }

        return item;
      })
    );
  };

  const handleAddComponent = () => {
    if (!createdData?.title) {
      toast("error", "Component can not be empty");
      return;
    }

    if (
      salaryBreakdown?.find(
        (item) =>
          item?.generatedId === createdData?.generatedId &&
          item?.title === createdData?.title
      )
    ) {
    } else {
      if (salaryBreakdown?.find((item) => item?.title === createdData?.title)) {
        toast("error", "Component already exist");
        return;
      }
    }

    if (createdData?.generatedId) {
      setSalaryBreakdown((prev) =>
        prev?.map((item) => {
          if (item?.generatedId === createdData?.generatedId) {
            return {
              ...item,
              ...createdData,
            };
          }
          return item;
        })
      );

      setCreatedData(undefined);
      return;
    }

    setSalaryBreakdown((prev) =>
      prev
        ? [
            ...prev,
            {
              ...createdData,
              key: createdData?.key || "",
              generatedId: generateUUID(),
            } as SalaryBreakdownType,
          ]
        : [
            {
              ...createdData,
              key: createdData?.key || "",
              generatedId: generateUUID(),
            } as SalaryBreakdownType,
          ]
    );

    setCreatedData(undefined);
  };

  useEffect(() => {
    setSalaryBreakdown(
      getSalaryData?.salary_breakdown?.map((item) => ({
        ...item,
        amount: 0,
        is_overwritten: 1,
        generatedId: generateUUID(),
      }))
    );
  }, [getSalaryData?.salary_breakdown]);

  function findData(type: "ADD" | "EDIT" | "DELETE") {
    return salaryBreakdown
      ?.filter((item) => {
        if (type === "EDIT" || type === "DELETE") {
          return item?.type === type && item?.id;
        }

        return item?.type === type;
      })
      ?.map((item) => ({
        ...item,
        value: item?.value?.toString() || "",
        type: undefined,
        is_overwritten: undefined,
        amount: undefined,
        is_default: undefined,
        generatedId: undefined,
      }));
  }

  const finalData = {
    updated_data: findData("EDIT"),
    added_data: findData("ADD"),
    delete_data: findData("DELETE"),
  };

  const onFinish = () => {
    if (totalPercentage !== 100) {
      toast("error", "Total sum of the components must be 100%");
      return;
    }

    if (data?.data?.payroll_setting?.id) {
      updateGrossComponent({
        id: data?.data?.payroll_setting?.id,
        body: finalData,
      });
    }
  };

  useEffect(() => {
    if (createdData && inputRef.current) {
      inputRef.current.focus();
    }
  }, [createdData]);

  useEffect(() => {
    if (isUpdateSuccess) {
      toast("success", "Salary breakdown updated successfully!");
      refetch();
    }
  }, [isUpdateSuccess, refetch]);

  // const checkModifiedDataLength = Object.values(finalData)?.filter(
  //   (item) => item?.length
  // )?.length;

  return (
    <SidebarLayout title="Salary Breakdown" tabList={payrollSettingsTabList}>
      {open?.type === "CONFIRM" && (
        <ConfirmModal
          open={open?.open}
          handleClose={handleClose}
          handleOk={() => {
            handleSalaryBreakdown(open?.data?.key || "", "type", "DELETE");
            toast("success", "Component deleted successfully!");
            handleClose();
          }}
          confirmTitle="Confirm"
          questionText={`Are you sure you want to delete the component called " ${
            open?.data?.title || ""
          } " ?`}
          okText="Yes"
          cancelText="No"
        />
      )}

      <div className="max-w-[800px] p-6 bg-white shadow-md rounded-lg">
        <Title level={4} className="text-[#2D3643]">
          Breakdown of Gross Salary
        </Title>
        <div className="border-b border-b-[#E9EFF6] my-6" />
        <Text className="block mb-4 text-gray-600 font-medium text-[#5e718d]">
          Gross Salary is the amount of salary that includes all the benefits
          and allowances but except any taxable amount. Total sum of breakdown
          must be 100%.
        </Text>
        {isLoading ? (
          <Loader />
        ) : (
          <Form className="custom-form" onFinish={onFinish}>
            {salaryBreakdown
              ?.filter((item) => item?.type !== "DELETE")
              ?.map((comp) => (
                <div
                  className="flex justify-between items-center py-2 border-b-[#E9EFF6] border-b"
                  key={comp.key}
                >
                  <div className="flex gap-4">
                    <div
                      className={`text-base font-medium leading-7 flex items-center gap-2 ${
                        comp.is_active ? "text-header" : "text-body-text"
                      }`}
                    >
                      {!comp.is_active ? (
                        <Tooltip
                          placement="top"
                          title="This label is inactive therefore it's value will not affect total value"
                          className="text-display-medium"
                        >
                          <EyeInvisibleFilled size={22} />

                          {/* <IoMdEyeOff size={22} /> */}
                        </Tooltip>
                      ) : null}
                      {comp.title}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        className={`!text-[#4A72FF] !border-[#B4C4FF] ${roundButtonClass}`}
                        onClick={() => setCreatedData(comp)}
                      >
                        <EditIcon />
                      </Button>
                      {!comp.is_default && (
                        <Button
                          className={`!text-dark-highlight !border-[#FFC5C1]  ${roundButtonClass}`}
                          onClick={() =>
                            handleOpen({
                              type: "CONFIRM",
                              data: {
                                key: comp.key,
                                title: comp.title || "",
                              },
                            })
                          }
                        >
                          <DeleteIcon />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 end-from-item min-h-[46px]">
                    {comp?.is_active ? (
                      <Form.Item
                        name={["salary", comp.key]}
                        className="mb-0"
                        validateFirst
                        rules={[
                          comp.key === "basic_salary"
                            ? {
                                type: "number",
                                max: 100,
                                message: "Percentage cannot be more than 100",
                              }
                            : {
                                type: "number",
                                max: 99,
                                message: "Percentage cannot be more than 99",
                              },
                          {
                            type: "number",
                            min: 0,
                            message: "Percentage cannot be negative",
                          },
                        ]}
                        initialValue={comp.value || undefined}
                      >
                        <InputNumber
                          size="large"
                          placeholder="0"
                          style={{ width: 120 }}
                          suffix={<PercentageIcon />}
                          value={comp.value || undefined}
                          onChange={(val) =>
                            handleSalaryBreakdown(
                              comp.key,
                              "value",
                              Number(val)
                            )
                          }
                        />
                      </Form.Item>
                    ) : null}
                  </div>
                </div>
              ))}

            {createdData && (
              <div className="flex items-center justify-between gap-2 my-4">
                <Input
                  placeholder="Type component name..."
                  className="flex-1 max-w-60 border-[#AFBACA]"
                  value={createdData?.title || ""}
                  onChange={(e) => handleCreatedData("title", e?.target?.value)}
                  size="large"
                  ref={inputRef}
                  disabled={
                    createdData?.key === "basic_salary" ||
                    createdData?.key === "house_rent" ||
                    createdData?.key === "medical_allowance" ||
                    createdData?.key === "conveyance"
                  }
                />

                <div className="flex items-center gap-2">
                  {createdData.key !== "basic_salary" ? (
                    <label
                      className="flex gap-2 items-center text-sm font-medium text-header cursor-pointer"
                      htmlFor="active-inactive"
                    >
                      <Switch
                        id="active-inactive"
                        checked={Boolean(createdData?.is_active)}
                        onChange={(checked) =>
                          handleCreatedData("is_active", Number(checked))
                        }
                      />
                      <span className="select-none">In-Active</span>
                    </label>
                  ) : null}

                  <label
                    className="flex gap-2 items-center text-sm font-medium text-header cursor-pointer"
                    htmlFor="taxable"
                  >
                    <Switch
                      id="taxable"
                      checked={Boolean(createdData?.taxable)}
                      onChange={(checked) =>
                        handleCreatedData("taxable", Number(checked))
                      }
                    />
                    <span className="select-none">Non-Taxable</span>
                  </label>
                </div>
                <div className="flex items-center gap-2.5">
                  <Button
                    className={`!text-[#1DB469] !border-[#8FE7B8] ${roundButtonClass}`}
                    onClick={() => {
                      handleAddComponent();
                    }}
                  >
                    <DoubleTick />
                  </Button>
                  <Button
                    className={`!text-dark-highlight !border-[#FFC5C1] ${roundButtonClass} px-2`}
                    onClick={() => setCreatedData(undefined)}
                  >
                    <XIcon />
                  </Button>
                </div>
              </div>
            )}
            <div className="py-6 px-4 bg-[#FFF5F4] flex justify-between items-center mt-2">
              <div>
                {!createdData && (
                  <Button
                    className="!border-dark-highlight !text-dark-highlight font-semibold middle-button"
                    icon={<AddIcon />}
                    onClick={() =>
                      setCreatedData({
                        is_active: 0,
                        taxable: 0,
                        value: 0,
                      })
                    }
                  >
                    Add Component
                  </Button>
                )}
              </div>
              <div className="flex flex-col items-end">
                <div className="text-lg flex gap-2 font-semibold">
                  <span>Total</span>
                  <span>{totalPercentage}%</span>
                </div>
                {totalPercentage !== 100 && (
                  <div className="text-dark-highlight">
                    Total sum of the components must be 100%
                  </div>
                )}
              </div>
            </div>
            <Space className="w-full mt-4" size={20}>
              {/* <Button className="normal-dark-button modal-button" size="middle">
                Cancel
              </Button> */}
              <Button
                className="modal-button"
                type="primary"
                htmlType="submit"
                size="middle"
                // disabled={!checkModifiedDataLength}
                loading={isUpdateLoading}
              >
                Update
              </Button>

              <span className="w-full text-primary font-normal text-base">
                Please click the <span className="font-semibold">'Update'</span>{" "}
                button to apply any changes
              </span>
            </Space>
          </Form>
        )}
      </div>
    </SidebarLayout>
  );
}
